export type VoltoBlock = {
  '@type'?: string;
  block_followup?: boolean;
  block_hide?: boolean;
  block_nav_title?: string | { id: string; defaultMessage?: string };
  block_title?: string | { id: string; defaultMessage?: string };
  blocks?: Record<string, VoltoBlock>;
  blocks_layout?: { items?: string[] };
  data?: {
    blocks?: Record<string, VoltoBlock>;
    blocks_layout?: { items?: string[] };
  };
  heading?: string;
  tag?: string;
  showOnDesktop?: boolean;
  showOnMobile?: boolean;
};

type BlockVisitor = (
  id: string,
  block: VoltoBlock,
  ancestors: VoltoBlock[],
) => void | boolean;

interface TraverseOptions {
  /**
   * When this returns true for a block, that block is neither visited nor
   * descended into (its whole subtree is pruned), while iteration continues
   * with its siblings. Mirrors the rendered DOM: e.g. a `block_hide` block
   * produces no markup, so nothing inside it exists either.
   */
  skip?: (id: string, block: VoltoBlock, ancestors: VoltoBlock[]) => boolean;
}

const getOrderedIds = (
  blocks: Record<string, VoltoBlock>,
  layoutItems?: string[],
) => (layoutItems?.length ? layoutItems : Object.keys(blocks));

export const traverseBlocks = (
  blocks: Record<string, VoltoBlock> | undefined,
  layoutItems: string[] | undefined,
  visitor: BlockVisitor,
  options?: TraverseOptions,
  ancestors: VoltoBlock[] = [],
) => {
  if (!blocks) return;

  getOrderedIds(blocks, layoutItems).some((id) => {
    const block = blocks[id];
    if (!block) return false;

    if (options?.skip?.(id, block, ancestors)) {
      return false;
    }

    if (visitor(id, block, ancestors) === false) {
      return true;
    }

    const childAncestors = [...ancestors, block];
    traverseBlocks(
      block.blocks,
      block.blocks_layout?.items,
      visitor,
      options,
      childAncestors,
    );
    traverseBlocks(
      block.data?.blocks,
      block.data?.blocks_layout?.items,
      visitor,
      options,
      childAncestors,
    );
    return false;
  });
};

export const hasBlockType = (
  blocks: Record<string, VoltoBlock> | undefined,
  type: string,
) => {
  let found = false;

  traverseBlocks(blocks, undefined, (_id, block) => {
    if (block['@type'] === type) {
      found = true;
      return false;
    }
    return undefined;
  });

  return found;
};
